import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Settings,
  Truck,
  Zap,
  Check,
  Info,
  Edit2,
  X,
} from 'lucide-react';
import { deliveryService } from '@services/delivery.service';
import type { DeliverySurcharge, VehicleRule } from '@services/delivery.service';
import { PageGuard } from '@components/PageGuard';
import { cn } from '@lib/utils';

// ── Types ─────────────────────────────────────────────────────────────────────

type SettingsTab = 'surcharges' | 'vehicles' | 'fleet';

interface SettingsNavItem {
  id: SettingsTab;
  label: string;
  icon: React.ElementType;
}

const TABS: SettingsNavItem[] = [
  { id: 'surcharges', label: 'Surcharge Rates',      icon: Zap },
  { id: 'vehicles',   label: 'Vehicle Rules',         icon: Truck },
  { id: 'fleet',      label: 'Chekit Fleet Settings', icon: Settings },
];

// ── Surcharge type labels ─────────────────────────────────────────────────────

const SURCHARGE_LABELS: Record<string, string> = {
  thermal_bag:       'Thermal Bag',
  dry_ice:           'Dry Ice',
  fragile_handling:  'Fragile Handling',
  hazmat:            'Hazmat',
  night_pct:         'Night Surcharge',
  signature:         'Signature Required',
  oversized:         'Oversized Package',
  special_packaging: 'Special Packaging',
};

const SURCHARGE_DESC: Record<string, string> = {
  thermal_bag:       'Applied when order requires temperature control (chilled/frozen)',
  dry_ice:           'Applied when dry ice packaging is required',
  fragile_handling:  'Applied for fragile or very_fragile items',
  hazmat:            'Applied for hazardous materials requiring certified rider',
  night_pct:         'Percentage surcharge on gross delivery fee for night deliveries',
  signature:         'Applied when delivery requires a signature on receipt',
  oversized:         'Applied when package dimensions exceed standard thresholds',
  special_packaging: 'Applied when special packaging is required for the shipment',
};


// ── Surcharge edit modal ──────────────────────────────────────────────────────

function SurchargeEditModal({
  surcharge,
  onClose,
  onSave,
  saving,
}: {
  surcharge: DeliverySurcharge;
  onClose: () => void;
  onSave: (value: number, valueType: 'flat' | 'percentage') => void;
  saving: boolean;
}) {
  const derivedType: 'flat' | 'percentage' = surcharge.flatFee != null ? 'flat' : 'percentage';
  const derivedValue = surcharge.flatFee ?? surcharge.percentage ?? 0;
  const [value, setValue]         = useState(derivedValue);
  const [valueType, setValueType] = useState<'flat' | 'percentage'>(derivedType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-md mx-4 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-[18px] font-semibold text-[var(--text-primary)]">
            Edit {SURCHARGE_LABELS[surcharge.surchargeType] ?? surcharge.surchargeType}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
            Value type
          </label>
          <select
            value={valueType}
            onChange={(e) => setValueType(e.target.value as 'flat' | 'percentage')}
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
          >
            <option value="flat">Flat fee (KES)</option>
            <option value="percentage">Percentage (%)</option>
          </select>
        </div>

        <div>
          <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
            {valueType === 'flat' ? 'Amount (KES)' : 'Percentage (%)'}
          </label>
          <input
            type="number"
            min={0}
            max={valueType === 'percentage' ? 100 : undefined}
            step={valueType === 'percentage' ? 1 : 5}
            value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(value, valueType)}
            disabled={saving}
            className="rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vehicle detail modal ──────────────────────────────────────────────────────

function VehicleModal({ rule, onClose }: { rule: VehicleRule; onClose: () => void }) {
  const boolChip = (v: boolean) =>
    v ? (
      <span className="rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[var(--state-success-bg)] text-[var(--state-success)]">Yes</span>
    ) : (
      <span className="rounded-full px-2.5 py-1 text-[11px] font-semibold bg-[var(--bg-base)] text-[var(--text-muted)]">No</span>
    );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,61,30,0.45)]">
      <div className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-lg mx-4 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-[18px] font-semibold text-[var(--text-primary)]">{rule.displayName}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:bg-[var(--bg-base)] hover:text-[var(--text-primary)] transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-4 text-[13px]">
          <div className="bg-[var(--bg-base)] rounded-2xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1">Max weight</p>
            <p className="font-semibold text-[var(--text-primary)]">{(rule.maxWeightGrams / 1000).toFixed(1)} kg</p>
          </div>
          <div className="bg-[var(--bg-base)] rounded-2xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1">Max distance</p>
            <p className="font-semibold text-[var(--text-primary)]">{rule.maxDistanceKm} km</p>
          </div>
          <div className="bg-[var(--bg-base)] rounded-2xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1">Max dims (L×W×H)</p>
            <p className="font-semibold text-[var(--text-primary)]">
              {rule.maxLengthMm}×{rule.maxWidthMm}×{rule.maxHeightMm} mm
            </p>
          </div>
          <div className="bg-[var(--bg-base)] rounded-2xl p-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)] mb-1">Display order</p>
            <p className="font-semibold text-[var(--text-primary)]">{rule.displayOrder}</p>
          </div>
        </div>

        <div className="space-y-2">
          {[
            { label: 'Enclosed vehicle',   val: rule.isEnclosed },
            { label: 'Hazmat eligible',    val: rule.isHazmatEligible },
            { label: 'Night eligible',     val: rule.isNightEligible },
            { label: 'Supports multi-stop', val: rule.supportsMultiStop },
          ].map(({ label, val }) => (
            <div key={label} className="flex items-center justify-between py-2 border-b border-[var(--border-subtle)] last:border-0">
              <span className="text-[13px] text-[var(--text-muted)]">{label}</span>
              {boolChip(val)}
            </div>
          ))}
        </div>

        <div className="rounded-2xl bg-[var(--state-warning-bg)] border border-[var(--state-warning-border)] p-3 flex items-start gap-2 text-[12px] text-[var(--state-warning)]">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          Editing vehicle rules is read-only for now.
          TODO: wire to <code>PUT /admin/delivery/vehicle-rules/{'{'}vehicleClass{'}'}</code>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const DeliverySettingsPage = () => {
  const qc = useQueryClient();
  const [activeTab, setActiveTab]           = useState<SettingsTab>('surcharges');
  const [editingSurcharge, setEditingSurcharge] = useState<DeliverySurcharge | null>(null);
  const [viewingVehicle, setViewingVehicle] = useState<VehicleRule | null>(null);

  // Fleet settings local state
  const [acceptTimeoutMinutes, setAcceptTimeoutMinutes]     = useState(15);
  const [tier1WindowSeconds, setTier1WindowSeconds]         = useState(30);
  const [tier2WindowSeconds, setTier2WindowSeconds]         = useState(120);
  const [tier3WindowSeconds, setTier3WindowSeconds]         = useState(180);
  const [maxConcurrentOrders, setMaxConcurrentOrders]       = useState(5);

  const { data: surcharges = [], isLoading: surchargesLoading } = useQuery({
    queryKey: ['delivery', 'surcharges'],
    queryFn: () => deliveryService.listSurcharges(),
  });

  const { data: vehicleRules = [], isLoading: vehiclesLoading } = useQuery({
    queryKey: ['delivery', 'vehicle-rules'],
    queryFn: () => deliveryService.listVehicleRules(),
  });

  const { data: fleetSettings, isLoading: fleetLoading } = useQuery({
    queryKey: ['delivery', 'fleet-settings'],
    queryFn: () => deliveryService.getFleetSettings(),
  });

  useEffect(() => {
    if (fleetSettings) {
      setAcceptTimeoutMinutes(fleetSettings.acceptTimeoutMinutes);
      setTier1WindowSeconds(fleetSettings.tier1WindowSeconds);
      setTier2WindowSeconds(fleetSettings.tier2WindowSeconds);
      setTier3WindowSeconds(fleetSettings.tier3WindowSeconds);
      setMaxConcurrentOrders(fleetSettings.maxConcurrentOrders);
    }
  }, [fleetSettings]);

  const surchargeMutation = useMutation({
    mutationFn: ({
      type,
      value,
      valueType,
    }: {
      type: string;
      value: number;
      valueType: 'flat' | 'percentage';
    }) => deliveryService.updateSurcharge(type, { value, valueType }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['delivery', 'surcharges'] });
      setEditingSurcharge(null);
    },
  });

  const fleetMutation = useMutation({
    mutationFn: () =>
      deliveryService.updateFleetSettings({
        acceptTimeoutMinutes,
        tier1WindowSeconds,
        tier2WindowSeconds,
        tier3WindowSeconds,
        maxConcurrentOrders,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['delivery', 'fleet-settings'] });
    },
  });

  const capabilityChip = (v: boolean) =>
    v ? (
      <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[var(--state-success-bg)] text-[var(--state-success)]">Yes</span>
    ) : (
      <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[var(--bg-base)] text-[var(--text-muted)]">No</span>
    );

  return (
    <PageGuard permission="settings.read">
      <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
        {/* Page header */}
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
            Delivery / Settings
          </p>
          <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
            Delivery Settings
          </h1>
          <p className="text-[14px] text-[var(--text-muted)]">
            Surcharge rates, vehicle rules, and Chekit fleet configuration
          </p>
        </div>

        {/* Layout */}
        <div className="flex gap-6">
          {/* Left nav */}
          <aside className="w-[240px] flex-shrink-0">
            <div className="rounded-3xl bg-white shadow-soft p-3 sticky top-8">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-[14px] font-medium transition-all mb-1 last:mb-0',
                      isActive
                        ? 'font-semibold text-[var(--text-primary)] bg-[var(--bg-elevated)] border-l-2 border-[var(--gradient-to)]'
                        : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-base)]',
                    )}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 space-y-6">

            {/* ── Surcharge rates ── */}
            {activeTab === 'surcharges' && (
              <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
                <div className="p-8 border-b border-[var(--border-subtle)]">
                  <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Surcharge Rates</h2>
                  <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
                    Per-delivery surcharges applied on top of the base delivery fee.
                    Sourced from the <code className="bg-[var(--bg-base)] px-1 rounded">delivery_surcharges</code> table.
                  </p>
                </div>

                {surchargesLoading ? (
                  <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">Loading...</div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-[var(--bg-base)]">
                      <tr>
                        <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Surcharge
                        </th>
                        <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Type
                        </th>
                        <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Value
                        </th>
                        <th className="px-6 py-4" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-subtle)]">
                      {surcharges.map((s) => (
                        <tr key={s.id} className="hover:bg-[var(--bg-base)] transition-colors">
                          <td className="px-6 py-4">
                            <p className="text-[14px] font-medium text-[var(--text-primary)]">
                              {SURCHARGE_LABELS[s.surchargeType] ?? s.surchargeType}
                            </p>
                            <p className="text-[12px] text-[var(--text-muted)]">
                              {SURCHARGE_DESC[s.surchargeType] ?? ''}
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            <span
                              className={cn(
                                'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                                s.flatFee != null
                                  ? 'bg-[var(--bg-elevated)] text-[var(--text-accent)]'
                                  : 'bg-[var(--bg-elevated)] text-[var(--text-accent)]',
                              )}
                            >
                              {s.flatFee != null ? 'flat' : 'percentage'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-[15px] font-semibold text-[var(--text-primary)]">
                              {s.flatFee != null
                                ? `KES ${s.flatFee.toFixed(2)}`
                                : `${s.percentage ?? 0}%`}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => setEditingSurcharge(s)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-default)] bg-white px-3 py-1.5 text-[12px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200"
                            >
                              <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── Vehicle rules ── */}
            {activeTab === 'vehicles' && (
              <div className="rounded-3xl bg-white shadow-soft overflow-hidden">
                <div className="p-8 border-b border-[var(--border-subtle)]">
                  <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Vehicle Rules</h2>
                  <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
                    Vehicle capabilities used by the 6-gate dispatch algorithm.
                    Sourced from <code className="bg-[var(--bg-base)] px-1 rounded">vehicle_selection_rules</code>.
                  </p>
                </div>

                {vehiclesLoading ? (
                  <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">Loading...</div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-[var(--bg-base)]">
                      <tr>
                        <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Vehicle
                        </th>
                        <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Max weight
                        </th>
                        <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Max distance
                        </th>
                        <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Enclosed
                        </th>
                        <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Hazmat
                        </th>
                        <th className="text-left px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                          Multi-stop
                        </th>
                        <th className="px-6 py-4" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--border-subtle)]">
                      {vehicleRules
                        .slice()
                        .sort((a, b) => a.displayOrder - b.displayOrder)
                        .map((v) => (
                          <tr key={v.id} className="hover:bg-[var(--bg-base)] transition-colors">
                            <td className="px-6 py-4">
                              <p className="text-[14px] font-medium text-[var(--text-primary)]">{v.displayName}</p>
                              <p className="text-[12px] text-[var(--text-muted)] font-mono">{v.vehicleClass}</p>
                            </td>
                            <td className="px-6 py-4 text-[13px] text-[var(--text-primary)]">
                              {(v.maxWeightGrams / 1000).toFixed(1)} kg
                            </td>
                            <td className="px-6 py-4 text-[13px] text-[var(--text-primary)]">
                              {v.maxDistanceKm} km
                            </td>
                            <td className="px-6 py-4">{capabilityChip(v.isEnclosed)}</td>
                            <td className="px-6 py-4">{capabilityChip(v.isHazmatEligible)}</td>
                            <td className="px-6 py-4">{capabilityChip(v.supportsMultiStop)}</td>
                            <td className="px-6 py-4 text-right">
                              <button
                                onClick={() => setViewingVehicle(v)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-default)] bg-white px-3 py-1.5 text-[12px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200"
                              >
                                <Info className="w-3.5 h-3.5" strokeWidth={1.5} />
                                Details
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── Chekit fleet settings ── */}
            {activeTab === 'fleet' && (
              <div className="rounded-3xl bg-white shadow-soft p-8 space-y-6">
                <div className="border-b border-[var(--border-subtle)] pb-5">
                  <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Chekit Fleet Settings</h2>
                  <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
                    Global defaults for the Chekit dispatch engine
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Accept timeout (minutes)
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={60}
                      value={acceptTimeoutMinutes}
                      onChange={(e) => setAcceptTimeoutMinutes(Number(e.target.value))}
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                    <p className="text-[12px] text-[var(--text-muted)] mt-1.5">
                      How long a merchant has to accept/decline a basket before auto-cancel
                    </p>
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                      Max concurrent orders per rider
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={maxConcurrentOrders}
                      onChange={(e) => setMaxConcurrentOrders(Number(e.target.value))}
                      className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                    />
                    <p className="text-[12px] text-[var(--text-muted)] mt-1.5">
                      System default, overridden by rider-level <code>max_concurrent_orders</code>
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-4">
                    Broadcast tier window durations
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { label: 'Tier 1 window (preferred rider)', val: tier1WindowSeconds, set: setTier1WindowSeconds },
                      { label: 'Tier 2 window (own + assigned pool)', val: tier2WindowSeconds, set: setTier2WindowSeconds },
                      { label: 'Tier 3 window (merchant invited / applied)', val: tier3WindowSeconds, set: setTier3WindowSeconds },
                    ].map(({ label, val, set }) => (
                      <div key={label}>
                        <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
                          {label}
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={10}
                            max={600}
                            step={10}
                            value={val}
                            onChange={(e) => set(Number(e.target.value))}
                            className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 pr-14 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors"
                          />
                          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[12px] text-[var(--text-muted)]">
                            sec
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-6 border-t border-[var(--border-subtle)] flex justify-end gap-3">
                  <button
                    onClick={() => {
                      if (fleetSettings) {
                        setAcceptTimeoutMinutes(fleetSettings.acceptTimeoutMinutes);
                        setTier1WindowSeconds(fleetSettings.tier1WindowSeconds);
                        setTier2WindowSeconds(fleetSettings.tier2WindowSeconds);
                        setTier3WindowSeconds(fleetSettings.tier3WindowSeconds);
                        setMaxConcurrentOrders(fleetSettings.maxConcurrentOrders);
                      }
                    }}
                    className="rounded-full border border-[var(--border-default)] bg-white px-5 py-2.5 text-[14px] font-medium text-[var(--text-primary)] hover:border-[var(--border-strong)] transition-all duration-200"
                  >
                    Discard
                  </button>
                  <button
                    onClick={() => fleetMutation.mutate()}
                    disabled={fleetMutation.isPending || fleetLoading}
                    className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-5 py-2.5 text-[14px] font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
                  >
                    <Check className="w-4 h-4" strokeWidth={1.5} />
                    {fleetMutation.isPending ? 'Saving...' : 'Save Fleet Settings'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Surcharge edit modal */}
      {editingSurcharge && (
        <SurchargeEditModal
          surcharge={editingSurcharge}
          onClose={() => setEditingSurcharge(null)}
          onSave={(value, valueType) =>
            surchargeMutation.mutate({
              type: editingSurcharge.surchargeType,
              value,
              valueType,
            })
          }
          saving={surchargeMutation.isPending}
        />
      )}

      {/* Vehicle detail modal */}
      {viewingVehicle && (
        <VehicleModal rule={viewingVehicle} onClose={() => setViewingVehicle(null)} />
      )}
    </PageGuard>
  );
};
